import * as React from 'react';
import {useSearchParams} from 'react-router-dom';
import {AnalystView} from 'src/AnalystView/AnalystView';
import {AnalyticsFlowView} from 'src/AnalyticsFlowView/AnalyticsFlowView';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {cn} from 'src/lib/utils';
import {TrendsView} from 'src/TrendsView/TrendsView';

type AnalyticsTab = 'flow' | 'trends' | 'insights';

const TABS: {key: AnalyticsTab; label: string}[] = [
  {key: 'flow', label: 'Flow'},
  {key: 'trends', label: 'Trends'},
  {key: 'insights', label: 'Insights'},
];

/**
 * The analytics page: one route, tabbed views. The active tab lives in the
 * URL (?tab=trends) so a view can be linked and survives a reload; the
 * default tab keeps a clean URL.
 */
export const AnalyticsPage = () => {
  // The page owns the breadcrumb: both tabs are "Analytics", and the
  // Trends branch must not depend on the Flow view having mounted.
  useBreadcrumb({label: 'Analytics', reset: true});
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('tab');
  const tab: AnalyticsTab =
    param === 'trends' ? 'trends' : param === 'insights' ? 'insights' : 'flow';

  const selectTab = (next: AnalyticsTab) => {
    const updated = new URLSearchParams(searchParams);
    if (next === 'flow') {
      updated.delete('tab');
    } else {
      updated.set('tab', next);
    }
    setSearchParams(updated, {replace: true});
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-5xl px-4 pt-4">
        <div className="border-border flex gap-1 border-b" role="tablist">
          {TABS.map(({key, label}) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => selectTab(key)}
              className={cn(
                '-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors',
                tab === key
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'flow' ? (
        <AnalyticsFlowView />
      ) : tab === 'trends' ? (
        <TrendsView />
      ) : (
        <AnalystView />
      )}
    </div>
  );
};
